import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { adminLogin, adminLoginSelect, isAdminLoggedIn, setAdminSession } from "../api/adminClient";
import { useTheme } from "../context/ThemeContext";
import { ROUTES } from "./routes";
import "./login.css";

function IconSun({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function IconMoon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
      />
    </svg>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function IconEye({ open }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858 3.03a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgChoices, setOrgChoices] = useState([]);
  const [selectingOrg, setSelectingOrg] = useState(false);

  useEffect(() => {
    if (isAdminLoggedIn()) navigate(ROUTES.dashboard, { replace: true });
  }, [navigate]);

  const completeLogin = (res) => {
    const user = res.data.user;
    user.name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    setAdminSession(res.data.token, user);
    toast.success(`Welcome to ${user.tenant_name || "your workspace"}`);
    navigate(ROUTES.dashboard);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(email.trim(), password);
      if (res.data?.requires_tenant_selection && res.data?.organizations?.length > 1) {
        setOrgChoices(res.data.organizations);
        setSelectingOrg(true);
        return;
      }
      completeLogin(res);
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = async (org) => {
    setLoading(true);
    try {
      const res = await adminLoginSelect(
        email.trim(),
        password,
        org.tenant_id,
        org.user_id
      );
      completeLogin(res);
    } catch (err) {
      toast.error(err.message || "Could not sign in to this organization");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setSelectingOrg(false);
    setOrgChoices([]);
  };

  return (
    <div className="admin-login-glass-page" data-theme={theme}>
      <Toaster position="top-center" />

      <div className="admin-login-glass-bg" aria-hidden>
        <div className="admin-login-glass-grid" />
        <div className="admin-login-glass-glow admin-login-glass-glow--brand" />
        <div className="admin-login-glass-glow admin-login-glass-glow--cyan" />
      </div>

      <header className="admin-login-glass-header">
        <Link to={ROUTES.login} className="admin-login-glass-brand">
          <img src="/5x-logo-tagline.png" alt="5X Assessment" className="admin-login-glass-logo" />
        </Link>
        <nav className="admin-login-glass-nav" aria-label="Login navigation">
          <Link to={ROUTES.signup} className="admin-login-glass-nav-link">Create workspace</Link>
          <button
            type="button"
            className="admin-login-glass-theme"
            onClick={toggleTheme}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <IconSun /> : <IconMoon />}
          </button>
        </nav>
      </header>

      <main className="admin-login-glass-main">
        <section className="admin-login-glass-hero" aria-labelledby="login-hero-title">
          <p className="admin-login-glass-eyebrow">5X Assessment · Admin</p>
          <h1 id="login-hero-title" className="admin-login-glass-headline">
            Your workspace,
            <span className="admin-login-glass-headline-accent"> powered by NEXUS</span>
          </h1>
          <p className="admin-login-glass-lead">
            Sign in to run portfolio assessments, manage engagement, and generate
            reports — with AI-assisted workflows built for your organization.
          </p>
          <ul className="admin-login-glass-highlights">
            <li>Portfolio intelligence and report-ready insights</li>
            <li>Invites, reminders, and participant journeys</li>
            <li>Private, organization-scoped workspace</li>
          </ul>
        </section>

        <section className="admin-login-glass-card-wrap">
          {selectingOrg ? (
            <div className="admin-login-glass-card admin-login-card--orgs">
              <h2 className="admin-login-glass-card-title">Choose organization</h2>
              <p className="admin-login-glass-card-sub">
                Your email is registered with multiple workspaces. Select which one to open.
              </p>
              <ul className="admin-login-org-list">
                {orgChoices.map((org) => {
                  const name = org.tenant_name || "Workspace";
                  return (
                    <li key={org.user_id || org.tenant_id}>
                      <button
                        type="button"
                        className="admin-login-org-card"
                        disabled={loading}
                        onClick={() => handleSelectOrg(org)}
                      >
                        {org.tenant_logo ? (
                          <img src={org.tenant_logo} alt="" className="admin-login-org-logo" />
                        ) : (
                          <span className="admin-login-org-logo admin-login-org-logo--placeholder" aria-hidden>
                            {name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="admin-login-org-meta">
                          <span className="admin-login-org-name">{name}</span>
                          {org.role_name ? (
                            <span className="admin-login-org-role">{org.role_name}</span>
                          ) : null}
                        </span>
                        <span className="admin-login-org-arrow" aria-hidden>→</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="admin-login-org-back"
                onClick={handleBackToCredentials}
                disabled={loading}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="admin-login-glass-card">
              <h2 className="admin-login-glass-card-title">Sign in</h2>
              <p className="admin-login-glass-card-sub">
                Enter your admin credentials to access your workspace.
              </p>

              <label className="admin-login-glass-label" htmlFor="admin-login-email">Email</label>
              <div className="admin-login-glass-field">
                <span className="admin-login-glass-field-icon" aria-hidden>
                  <IconMail />
                </span>
                <input
                  id="admin-login-email"
                  type="email"
                  className="admin-login-glass-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <label className="admin-login-glass-label" htmlFor="admin-login-password">Password</label>
              <div className="admin-login-glass-field">
                <span className="admin-login-glass-field-icon" aria-hidden>
                  <IconLock />
                </span>
                <input
                  id="admin-login-password"
                  type={showPassword ? "text" : "password"}
                  className="admin-login-glass-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="admin-login-glass-field-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <IconEye open={showPassword} />
                </button>
              </div>

              <button type="submit" className="admin-login-glass-submit" disabled={loading}>
                {loading ? "Signing in…" : "Enter command center"}
              </button>

              <p className="admin-login-glass-foot">
                New organization?{" "}
                <Link to={ROUTES.signup} className="admin-login-glass-foot-link">
                  Create your workspace
                </Link>
              </p>
            </form>
          )}
        </section>
      </main>

      <footer className="admin-login-glass-page-foot">
        Secured admin access · 5X platform
      </footer>
    </div>
  );
}
